import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  AppState,
  Dimensions,
  Easing,
  Modal,
  Platform,
  Pressable,
  StatusBar,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useGlobalRealtime } from '../realtime/GlobalRealtimeProvider';
import { logCallLifecycleEnd } from './callLifecycleDebug';
import { navigationRef } from '../navigation/navigationRef';
import { useLocale } from '../i18n/LocaleContext';
import { tr } from '../i18n/runtime';
import { stopIncomingCallRingtone, playIncomingCallRingtone } from '../util/playIncomingCallRingtone';
import { dismissIncomingCallNotification } from '../notifications/androidChannels';
import type { IncomingCallUIPayload } from './incomingCallUiBridge';
import {
  callRouteParamsFromIncomingPayload,
  consumeIncomingCallAnswerIntent,
  dismissIncomingCallUI,
  getHiddenIncomingCall,
  hideIncomingCallUI,
  subscribeIncomingCallUI,
} from './incomingCallUiBridge';
import { AvatarBadge } from '../components/AvatarBadge';
import { incomingCallHeadline } from './callLabels';
import { showIncomingCallNotification } from '../notifications/callNotifications';
import { sendMessage } from '../api/client';
import { encodeTextPayload } from '../util/chatPayload';
import { voiceCallWarn } from './voiceCallDebug';
import type { CallsMessageKey } from '../i18n/areas/calls';

/** Готовые ответы «Ответить сообщением» — коротко и вежливо, как у системной звонилки. */
const QUICK_REPLY_KEYS: CallsMessageKey[] = [
  'callQuickReplyCantTalk',
  'callQuickReplyFewMinutes',
  'callQuickReplyTextMe',
];

/** Ключ звонка для эффектов: тот же звонок вторым каналом не должен перезапускать рингтон. */
function incomingCallKey(p: IncomingCallUIPayload | null): string | null {
  // Только чат: нога часто доезжает вторым кадром, и смена ключа заново запускала бы рингтон.
  return p ? p.chatId : null;
}

/** Сколько ждём навигатор, отвечая на звонок, принятый ещё из шторки (холодный старт). */
const AUTO_ANSWER_NAV_WAIT_MS = 8_000;

const { width: SCREEN_W } = Dimensions.get('window');

function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.trim().replace('#', '');
  const full =
    raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw.length === 6 ? raw : '';
  if (!full) return `rgba(0,0,0,${alpha})`;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0];
    const b = parts[1][0];
    if (a && b) return `${a}${b}`.toUpperCase();
  }
  const s = name.trim();
  if (s.length >= 2) return s.slice(0, 2).toUpperCase();
  return s.slice(0, 1).toUpperCase() || '?';
}

function IconPhone({ size, color }: { size: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path
        fill={color}
        d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1C10.61 21 3 13.39 3 3a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.24 1.02l-2.21 2.2z"
      />
    </Svg>
  );
}

function IconVideoCam({ size, color }: { size: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path
        fill={color}
        d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z"
      />
    </Svg>
  );
}

function IconMic({ size, color }: { size: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path
        fill={color}
        d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z"
      />
    </Svg>
  );
}

function IconClose({ size, color }: { size: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path
        fill={color}
        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
      />
    </Svg>
  );
}

/**
 * Полноэкранное окно входящего звонка.
 */
export function IncomingCallOverlay(): React.ReactElement | null {
  const { sendSignal } = useGlobalRealtime();
  const { t } = useLocale();
  const insets = useSafeAreaInsets();
  const [payload, setPayload] = useState<IncomingCallUIPayload | null>(null);
  const [pulse] = useState(() => new Animated.Value(1));
  const [glowPulse] = useState(() => new Animated.Value(0.35));

  /** «Ответить сообщением» раскрыт — показываем три готовых ответа над кнопками. */
  const [quickRepliesForKey, setQuickRepliesForKey] = useState<string | null>(null);
  /**
   * Звонок, которому человек сам выключил звук («Назад»). Развернув его снова из шторки, он
   * хочет ответить или отклонить, а не услышать рингтон второй раз.
   */
  const silencedCallKeyRef = useRef<string | null>(null);

  useEffect(() => {
    return subscribeIncomingCallUI(setPayload);
  }, []);

  const callKey = incomingCallKey(payload);
  const chatIdForRing = payload?.chatId ?? null;
  // Список привязан к звонку: следующий входящий открывается без него.
  const quickRepliesOpen = callKey !== null && quickRepliesForKey === callKey;

  useEffect(() => {
    if (!callKey || !chatIdForRing) return;
    let cancelled = false;
    void (async () => {
      /**
       * Полноэкранное уведомление о звонке само зациклило рингтон темы (канал `calls_*`
       * плюс `loopSound`) — снимаем его прежде, чем запускать свой. Иначе после тапа по
       * уведомлению звонок звонил бы в два голоса. Снимаем и при выключенном звуке,
       * и при заглушённом чате: уведомление ongoing, само оно не исчезнет.
       */
      await dismissIncomingCallNotification();
      if (silencedCallKeyRef.current === callKey) return;
      // Заглушённый чат звонок не глушит: «без звука» — про сообщения (аудит 2026-10-04),
      // пропустить звонок от того, кого заглушил ради болтовни в группе, хуже, чем услышать.
      if (!cancelled) await playIncomingCallRingtone();
    })();
    return () => {
      cancelled = true;
    };
  }, [callKey, chatIdForRing]);

  /**
   * «Звонит» — звонящему: экран входящего действительно показан (не «занято», не заглушён
   * дедупликацией). По этому сигналу у него «Вызов…» сменяется на «Звонит…». Один раз на
   * звонок: тот же звонок вторым каналом и разворот из шторки повторно его не шлют.
   */
  const ringingSentForRef = useRef<string | null>(null);
  const ringingInviteId = payload?.inviteId ?? null;
  useEffect(() => {
    if (!callKey || !chatIdForRing) return;
    const sentKey = `${chatIdForRing}|${ringingInviteId ?? ''}`;
    if (ringingSentForRef.current === sentKey) return;
    ringingSentForRef.current = sentKey;
    const leg = ringingInviteId ? { callInviteId: ringingInviteId } : {};
    sendSignal(chatIdForRing, { signalType: 'ringing', ...leg, payload: JSON.stringify(leg) });
  }, [callKey, chatIdForRing, ringingInviteId, sendSignal]);

  useEffect(() => {
    if (!callKey) {
      // Звонок ушёл совсем (а не свёрнут в шторку) — следующий из этого чата снова звонит.
      if (!getHiddenIncomingCall()) {
        silencedCallKeyRef.current = null;
        ringingSentForRef.current = null;
      }
    }
  }, [callKey]);

  /**
   * Возврат на передний план, пока звонок ещё звонит.
   *
   * <p>Уведомление могло появиться уже после того, как оверлей снял его при показе: пуш
   * доезжает позже WS-инвайта, а если в этот момент приложение было свёрнуто, фоновый
   * обработчик рисует шторку по праву. Открыв приложение, человек не должен видеть звонок
   * дважды — снимаем уведомление ещё раз, теперь по факту «экран виден».
   */
  useEffect(() => {
    if (!callKey) return;
    const sub = AppState.addEventListener('change', (state) => {
      if (state !== 'active') return;
      void dismissIncomingCallNotification();
    });
    return () => {
      sub.remove();
    };
  }, [callKey]);

  useEffect(() => {
    if (!callKey) return;
    const ring = Animated.loop(
      Animated.sequence([
        Animated.parallel([
          Animated.timing(pulse, {
            toValue: 1.06,
            duration: 1400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(glowPulse, {
            toValue: 0.55,
            duration: 1400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
        ]),
        Animated.parallel([
          Animated.timing(pulse, {
            toValue: 1,
            duration: 1400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(glowPulse, {
            toValue: 0.35,
            duration: 1400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
        ]),
      ]),
    );
    ring.start();
    return () => {
      ring.stop();
      pulse.setValue(1);
      glowPulse.setValue(0.35);
    };
  }, [callKey, pulse, glowPulse]);

  // Групповой входящий: заголовок — название группы, звонящий — в подзаголовке. Иначе
  // звонок в группу было не отличить от личного (E2E-8).
  const headline = useMemo(() => (payload ? incomingCallHeadline(payload) : null), [payload]);

  const subtitle = useMemo(() => {
    if (!payload) return '';
    if (headline?.callerName) return t('callGroupIncomingFrom').replace('{name}', headline.callerName);
    return payload.callMode === 'video' ? t('callIncomingVideoLine') : t('callIncomingAudioLine');
  }, [headline, payload, t]);

  const chipLabel = useMemo(() => {
    if (!payload) return '';
    if (payload.callScope === 'group') {
      return payload.callMode === 'video' ? t('callScopeBadgeGroupVideo') : t('callScopeBadgeGroupAudio');
    }
    return payload.callMode === 'video' ? t('callIncomingChipVideo') : t('callIncomingChipAudio');
  }, [payload, t]);

  const onDecline = useCallback(() => {
    if (!payload) return;
    logCallLifecycleEnd('incoming_call_overlay_decline', {
      chatId: payload.chatId,
      extra: { callMode: payload.callMode },
    });
    void stopIncomingCallRingtone();
    sendSignal(payload.chatId, { signalType: 'decline', payload: '{}' });
    dismissIncomingCallUI();
  }, [payload, sendSignal]);

  /**
   * @param audioOnly «Ответить без видео»: на видеозвонок — без своей камеры. Собеседника видно,
   *     камеру можно включить потом на панели звонка (§3 #31 аудита удобства).
   */
  const acceptCall = useCallback(
    (audioOnly: boolean) => {
      if (!payload) return;
      void stopIncomingCallRingtone();
      dismissIncomingCallUI();
      if (navigationRef.isReady()) {
        navigationRef.navigate('Call', {
          ...callRouteParamsFromIncomingPayload(payload),
          ...(audioOnly ? { audioOnly: true } : {}),
        });
      }
    },
    [payload],
  );
  const onAccept = useCallback(() => acceptCall(false), [acceptCall]);
  const onAcceptWithoutVideo = useCallback(() => acceptCall(true), [acceptCall]);

  /**
   * «Ответить» нажали ещё в шторке, а экран входящего поднялся только теперь (приложение
   * открывалось из фона или с нуля) — отвечаем сами, второго нажатия человек не ждёт.
   */
  useEffect(() => {
    if (!payload || !consumeIncomingCallAnswerIntent(payload.chatId)) return;
    let cancelled = false;
    void (async () => {
      const deadline = Date.now() + AUTO_ANSWER_NAV_WAIT_MS;
      while (!navigationRef.isReady() && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (!cancelled) onAccept();
    })();
    return () => {
      cancelled = true;
    };
  }, [onAccept, payload]);

  /**
   * Android «Назад» — свернуть, а не отклонить.
   *
   * <p>Раньше здесь стоял `onDecline`: самый частый жест Android, сделанный по привычке,
   * клал трубку звонящему (аудит удобства 2026-10-04, §3 #27). Теперь он только выключает
   * звук и убирает экран в шторку, где звонок по-прежнему можно принять или отклонить, а тап
   * разворачивает экран обратно. В вебе шторки нет — там «Назад» (Esc) только выключает звук,
   * иначе ответить было бы уже негде.
   */
  const onSilenceAndHide = useCallback(() => {
    if (!payload) return;
    logCallLifecycleEnd('incoming_call_overlay_hidden', {
      chatId: payload.chatId,
      extra: { callMode: payload.callMode },
    });
    silencedCallKeyRef.current = incomingCallKey(payload);
    void stopIncomingCallRingtone();
    if (Platform.OS !== 'android') return;
    const hidden = hideIncomingCallUI();
    if (hidden) {
      void showIncomingCallNotification(hidden, { quiet: true }).catch((e: unknown) => {
        voiceCallWarn('quiet incoming call notification failed', e);
      });
    }
  }, [payload]);

  /** «Ответить сообщением»: короткий текст в чат и отказ — звонящий сразу видит, почему. */
  const onQuickReply = useCallback(
    (text: string) => {
      if (!payload) return;
      void sendMessage(payload.chatId, encodeTextPayload(text)).catch((e: unknown) => {
        voiceCallWarn('quick reply to call failed', e);
      });
      onDecline();
    },
    [onDecline, payload],
  );

  if (!payload) {
    return null;
  }

  const avatarLetter = initialsFromName(payload.callerName);
  const isVideo = payload.callMode === 'video';
  const orbTint = isVideo ? '#4f46e5' : '#2563eb';

  return (
    <Modal
      visible
      animationType="fade"
      presentationStyle={Platform.OS === 'ios' ? 'fullScreen' : undefined}
      statusBarTranslucent
      onRequestClose={quickRepliesOpen ? () => setQuickRepliesForKey(null) : onSilenceAndHide}>
      <StatusBar barStyle="light-content" backgroundColor="#030712" />
      <View style={styles.root}>
        <View style={[StyleSheet.absoluteFill, { pointerEvents: 'none' }]}>
          <View style={[styles.ambientOrb, { backgroundColor: orbTint, opacity: 0.22, top: -SCREEN_W * 0.15 }]} />
          <View
            style={[
              styles.ambientOrbSmall,
              { backgroundColor: '#7c3aed', opacity: 0.12, bottom: SCREEN_W * 0.1, right: -40 },
            ]}
          />
          <View style={styles.vignette} />
        </View>

        <View style={[styles.flexMain, { paddingTop: insets.top + 12 }]}>
          <View style={styles.centerBlock}>
            <Text style={styles.overline}>{t('callIncomingFull')}</Text>

            <View style={styles.avatarStage}>
              <Animated.View
                style={[
                  styles.avatarPulseRing,
                  {
                    opacity: glowPulse,
                    borderColor: orbTint,
                  },
                ]}
              />
              <Animated.View style={[styles.avatarOuter, { transform: [{ scale: pulse }] }]}>
                <View
                  style={[
                    styles.avatarGlow,
                    Platform.OS === 'web'
                      ? { boxShadow: `0 0 56px ${hexToRgba(orbTint, 0.55)}` }
                      : { shadowColor: orbTint },
                  ]}
                />
                <LinearAvatarBorder tint={orbTint} />
                <View style={styles.avatarInner}>
                  {payload.callerUserId ? (
                    <AvatarBadge
                      userId={payload.callerUserId}
                      displayName={payload.callerName}
                      hasAvatar={payload.callerHasAvatar}
                      revision={payload.callerAvatarRevision ?? 0}
                      shapeId={payload.callerAvatarShape}
                      size={140}
                      outlineColor="rgba(248, 250, 252, 0.55)"
                    />
                  ) : (
                    <Text style={styles.avatarText}>{avatarLetter}</Text>
                  )}
                </View>
              </Animated.View>
            </View>

            <View style={styles.typeChip}>
              {isVideo ? (
                <IconVideoCam size={16} color="#a5b4fc" />
              ) : (
                <IconMic size={16} color="#93c5fd" />
              )}
              <Text style={[styles.typeChipText, styles.typeChipTextPad]}>{chipLabel}</Text>
            </View>

            <Text style={styles.name} numberOfLines={2}>
              {headline?.title || tr('callGeneric')}
            </Text>
            <Text style={styles.subtitle}>{subtitle}</Text>
          </View>

          <View style={[styles.dock, { paddingBottom: Math.max(insets.bottom, 20) }]}>
            {/*
              Дополнительные ответы — отдельной строкой НАД карточкой с кнопками, а не третьей
              кнопкой в ней: «Ответить» и «Отклонить» обязаны остаться на своих местах (по ним
              бьют и пальцы по привычке, и e2e-сценарии 07/12/14). Карточка прижата к низу, а
              эта строка растёт вверх.
            */}
            {quickRepliesOpen ? (
              <View style={styles.extraRow}>
                {QUICK_REPLY_KEYS.map((key) => (
                  <Pressable
                    key={key}
                    accessibilityRole="button"
                    accessibilityLabel={t(key)}
                    style={({ pressed }) => [styles.extraPill, styles.quickReplyPill, pressed && styles.pressed]}
                    onPress={() => onQuickReply(t(key))}>
                    <Text style={styles.extraPillText} numberOfLines={2}>
                      {t(key)}
                    </Text>
                  </Pressable>
                ))}
              </View>
            ) : (
              <View style={styles.extraRow}>
                {isVideo ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t('callAnswerWithoutVideo')}
                    style={({ pressed }) => [styles.extraPill, pressed && styles.pressed]}
                    onPress={onAcceptWithoutVideo}>
                    <IconMic size={16} color="#bbf7d0" />
                    <Text style={[styles.extraPillText, styles.extraPillTextPad]}>
                      {t('callAnswerWithoutVideo')}
                    </Text>
                  </Pressable>
                ) : null}
                {payload.callScope === 'direct' ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t('callReplyWithMessage')}
                    style={({ pressed }) => [styles.extraPill, pressed && styles.pressed]}
                    onPress={() => setQuickRepliesForKey(callKey)}>
                    <Text style={styles.extraPillText}>{t('callReplyWithMessage')}</Text>
                  </Pressable>
                ) : null}
              </View>
            )}
            <View style={styles.dockCard}>
              <View style={styles.actions}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('callBtnReject')}
                  style={({ pressed }) => [styles.hit, pressed && styles.pressed]}
                  onPress={onDecline}>
                  <View style={[styles.circleBtn, styles.declineBtn]}>
                    <IconClose size={26} color="#fff" />
                  </View>
                  <Text style={styles.actionLabel}>{t('callBtnReject')}</Text>
                </Pressable>

                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('callBtnAnswer')}
                  style={({ pressed }) => [styles.hit, pressed && styles.pressed]}
                  onPress={onAccept}>
                  <View style={[styles.circleBtn, styles.acceptBtn]}>
                    {isVideo ? (
                      <IconVideoCam size={30} color="#fff" />
                    ) : (
                      <IconPhone size={30} color="#fff" />
                    )}
                  </View>
                  <Text style={styles.actionLabel}>{t('callBtnAnswer')}</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/** Тонкое кольцо-градиент вокруг аватара без expo-linear-gradient */
function LinearAvatarBorder({ tint }: { tint: string }) {
  return (
    <View
      style={[
        StyleSheet.absoluteFillObject,
        {
          borderRadius: 76,
          borderWidth: 3,
          borderColor: tint,
          opacity: 0.85,
          pointerEvents: 'none',
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#030712',
  },
  flexMain: {
    flex: 1,
    justifyContent: 'space-between',
  },
  ambientOrb: {
    position: 'absolute',
    width: SCREEN_W * 1.1,
    height: SCREEN_W * 1.1,
    borderRadius: SCREEN_W * 0.55,
    alignSelf: 'center',
  },
  ambientOrbSmall: {
    position: 'absolute',
    width: 220,
    height: 220,
    borderRadius: 110,
  },
  vignette: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(3, 7, 18, 0.45)',
  },
  centerBlock: {
    alignItems: 'center',
    paddingHorizontal: 32,
    flexShrink: 1,
  },
  avatarStage: {
    width: 176,
    height: 176,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  overline: {
    color: 'rgba(148, 163, 184, 0.95)',
    fontSize: 12,
    letterSpacing: 2,
    textTransform: 'uppercase',
    fontWeight: '600',
    marginBottom: 28,
  },
  avatarPulseRing: {
    position: 'absolute',
    width: 172,
    height: 172,
    borderRadius: 86,
    borderWidth: 2,
  },
  avatarOuter: {
    width: 152,
    height: 152,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarGlow: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 76,
    ...Platform.select({
      web: {},
      default: {
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.55,
        shadowRadius: 28,
        elevation: 18,
      },
    }),
  },
  avatarInner: {
    width: 146,
    height: 146,
    borderRadius: 73,
    backgroundColor: 'rgba(30, 41, 59, 0.96)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 48,
    fontWeight: '700',
    color: '#f1f5f9',
    letterSpacing: -1,
  },
  typeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: 'rgba(30, 58, 138, 0.35)',
    borderWidth: 1,
    borderColor: 'rgba(129, 140, 248, 0.35)',
    marginBottom: 14,
  },
  typeChipText: {
    color: '#e0e7ff',
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  typeChipTextPad: {
    marginLeft: 8,
  },
  name: {
    color: '#f8fafc',
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    letterSpacing: -0.5,
    marginBottom: 8,
    lineHeight: 34,
    maxWidth: SCREEN_W - 48,
  },
  subtitle: {
    color: 'rgba(148, 163, 184, 0.95)',
    fontSize: 15,
    fontWeight: '500',
    textAlign: 'center',
  },
  dock: {
    paddingHorizontal: 20,
    paddingTop: 8,
  },
  extraRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 12,
  },
  // Высота не меньше 44 dp — минимум зоны нажатия (WCAG / Material).
  extraPill: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 22,
    backgroundColor: 'rgba(30, 41, 59, 0.85)',
    borderWidth: 1,
    borderColor: 'rgba(148, 163, 184, 0.25)',
  },
  quickReplyPill: {
    maxWidth: '100%',
  },
  extraPillText: {
    color: '#e2e8f0',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  extraPillTextPad: {
    marginLeft: 8,
  },
  dockCard: {
    borderRadius: 32,
    backgroundColor: 'rgba(15, 23, 42, 0.72)',
    borderWidth: 1,
    borderColor: 'rgba(148, 163, 184, 0.14)',
    paddingVertical: 26,
    paddingHorizontal: 16,
    overflow: 'hidden',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-evenly',
    width: '100%',
    maxWidth: 360,
    alignSelf: 'center',
  },
  hit: {
    alignItems: 'center',
    minWidth: 120,
  },
  pressed: {
    opacity: 0.88,
    transform: [{ scale: 0.98 }],
  },
  circleBtn: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  declineBtn: {
    backgroundColor: '#ef4444',
    ...Platform.select({
      web: { boxShadow: '0px 8px 16px rgba(248, 113, 113, 0.45)' },
      default: {
        shadowColor: '#f87171',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.45,
        shadowRadius: 16,
        elevation: 12,
      },
    }),
  },
  acceptBtn: {
    backgroundColor: '#22c55e',
    ...Platform.select({
      web: { boxShadow: '0px 8px 16px rgba(74, 222, 128, 0.45)' },
      default: {
        shadowColor: '#4ade80',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.45,
        shadowRadius: 16,
        elevation: 12,
      },
    }),
  },
  actionLabel: {
    color: '#e2e8f0',
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.2,
  },
});
